import { Link, router } from '@inertiajs/react'
import { ClipboardList, AlertCircle } from 'lucide-react'
import { BackofficeLayout } from '@/components/layout/BackofficeLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import { BackofficeBusinessTask } from '@/types'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { Plus } from 'lucide-react'


type Props = {
  tasks: BackofficeBusinessTask[]
  search: string
  status_filter: string
  pagination: PaginationMeta
}

export default function BusinessTasksIndex({ tasks, search, status_filter, pagination }: Props) {
  const handleFilter = (status: string) => {
    router.get('/backoffice/business_tasks', { status: status || undefined, search: search || undefined }, { preserveState: true })
  }

  return (
    <BackofficeLayout title="Business Tasks" breadcrumbs={[{ label: 'Backoffice', href: '/backoffice/dashboard' }, { label: 'Tasks' }]}>
      <div className="mb-8">
        <h1 className="text-xl font-semibold tracking-tight">Business Tasks</h1>
        <p className="text-sm text-muted-foreground">{pagination.total} {pagination.total === 1 ? 'task' : 'tasks'}</p>
        <div className="flex items-center gap-2 mt-3">
          <Button size="sm" variant="outline" asChild className="gap-1.5 text-[13px] h-8">
            <Link href="/backoffice/business_tasks/new">
              <Plus className="w-3.5 h-3.5" />
              Assign Task
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <SearchInput value={search} placeholder="Search tasks..." />
      </div>

      <div className="flex items-center gap-1.5 mb-4">
        {['', 'pending', 'responded', 'completed'].map(s => (
          <Button
            key={s}
            variant={status_filter === s ? 'default' : 'outline'}
            size="sm"
            className="h-7 text-xs"
            onClick={() => handleFilter(s)}
          >
            {s === '' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {tasks.length === 0 ? (
        <div className="border rounded-lg py-16 text-center">
          <ClipboardList className="w-6 h-6 mx-auto mb-3 text-muted-foreground/30" />
          <h3 className="text-sm font-medium text-foreground mb-1">No tasks found</h3>
        </div>
      ) : (
        <div className="border rounded-lg">
          {tasks.map((task, i) => (
            <Link
              key={task.id}
              href={`/backoffice/business_tasks/${task.id}`}
              className={`flex items-center justify-between px-4 py-3 hover:bg-muted/50 transition-colors ${i > 0 ? 'border-t' : ''}`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                  <ClipboardList className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground truncate">{task.type_name}</span>
                    <StatusBadge status={task.status} />
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{task.business_name}</span>
                    <span>·</span>
                    <span>{task.account_name}</span>
                    {task.due_date && (
                      <>
                        <span>·</span>
                        <span className={task.overdue ? 'text-red-600 font-medium flex items-center gap-1' : ''}>
                          {task.overdue && <AlertCircle className="w-3 h-3" />}
                          Due: {new Date(task.due_date).toLocaleDateString()}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      <div className="mt-4">
        <Pagination pagination={pagination} />
      </div>
    </BackofficeLayout>
  )
}
